import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { GeneticInformationObservationRecord } from '@models/genetic-information';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useListGeneticObservationsQuery,
  useGetGeneticObservationQuery,
  usePostGeneticObservationRecordMutation,
  useUpdateGeneticObservationRecordMutation,
  useDeleteGeneticObservationRecordMutation,
  type PostGeneticObservationRecordMutationVariables,
  type UpdateGeneticObservationRecordMutationVariables,
  type DeleteGeneticObservationRecordMutation,
  type DeleteGeneticObservationRecordMutationVariables,
} from 'generated/genetic-information.generated';

export const useGetGeneticInformation = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListGeneticObservationsQuery({
    variables: {
      input: {
        patient: patient_id,
        count: 1500,
        page: 1,
      },
    },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listGeneticObservations.data ?? []).map(
    (r) => new GeneticInformationObservationRecord(r)
  );
  return { ...response, data: records };
};

export const useGetGeneticInformationSingle = (params: {
  record_id: string;
  skip?: boolean;
}) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetGeneticObservationQuery({
    variables: {
      input: {
        record_id: params.record_id,
        patient_id,
      },
    },
    skip:
      !patient_id ||
      !params.record_id ||
      params.record_id === 'add' ||
      Boolean(params.skip),
  });

  const { data, error } = response;
  handleError(error);
  const record = data?.getGeneticObservation;
  return {
    ...response,
    data: record ? new GeneticInformationObservationRecord(record) : null,
  };
};

export function useCreateGeneticInformation(): [
  (
    params: PostGeneticObservationRecordMutationVariables
  ) => Promise<FetchResult<GeneticInformationObservationRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    usePostGeneticObservationRecordMutation();
  const loading = baseLoading;

  async function createGeneticInfo(
    params: PostGeneticObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postGeneticObservationRecord) {
          return;
        }
        const storeObject =
          response.postGeneticObservationRecord as unknown as StoreObject;
        try {
          cache.modify<{
            listGeneticInformationObservations: CachedRecordList;
          }>({
            fields: {
              listGeneticInformationObservations: (
                existing,
                { storeFieldName, toReference }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === params.input.subject?.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdGeneticInfo = response.data?.postGeneticObservationRecord;
    return {
      ...response,
      data: createdGeneticInfo
        ? new GeneticInformationObservationRecord(createdGeneticInfo)
        : undefined,
    };
  }
  return [createGeneticInfo, loading];
}

export function useUpdateGeneticInformation(): [
  (
    params: UpdateGeneticObservationRecordMutationVariables
  ) => Promise<FetchResult<GeneticInformationObservationRecord>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useUpdateGeneticObservationRecordMutation();
  const loading = baseLoading;

  async function updateGeneticInfo(
    params: UpdateGeneticObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedGeneticInfo = response.data?.updateGeneticObservationRecord;
    return {
      ...response,
      data: updatedGeneticInfo
        ? new GeneticInformationObservationRecord(updatedGeneticInfo)
        : undefined,
    };
  }
  return [updateGeneticInfo, loading];
}

export function useDeleteGeneticInformation(): [
  (
    params: DeleteGeneticObservationRecordMutationVariables
  ) => Promise<FetchResult<DeleteGeneticObservationRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useDeleteGeneticObservationRecordMutation();
  const loading = baseLoading;

  async function deleteGeneticInfo(
    params: DeleteGeneticObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteGeneticObservationRecord) {
          return;
        }
        const { observation_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'ObservationModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteGeneticInfo, loading];
}
